import { useColorScheme } from "@mui/material/styles";
import { useCallback } from "react";

/**
 * Adapter over MUI's color-scheme mechanism that preserves the historical
 * `{ themeMode, setThemeMode, toggleThemeMode }` shape, so existing callers stay
 * unchanged. Must be used inside the app's `ThemeProvider` (see EncampThemeProvider).
 *
 * `mode` is `undefined` on the first render and may be `"system"`; we resolve to
 * a concrete `"light" | "dark"` (defaulting to light, matching the app default).
 */
export function useThemeMode() {
  const { mode, systemMode, setMode } = useColorScheme();
  const resolved = mode === "system" ? systemMode : mode;
  const themeMode: "light" | "dark" = resolved === "dark" ? "dark" : "light";

  const setThemeMode = useCallback((next: "light" | "dark") => setMode(next), [setMode]);

  const toggleThemeMode = useCallback(
    () => setMode(themeMode === "dark" ? "light" : "dark"),
    [setMode, themeMode]
  );

  return { themeMode, setThemeMode, toggleThemeMode };
}
